/**
 * En qué memorias viene cada modelo.
 *
 * POR QUE ESTA TABLA EXISTE
 * -------------------------
 * La fila del modelo ya se arma desde el lineup: el 16 Pro aparece aunque no
 * tenga ni una ficha cargada, y dice «Sin cargar». Adentro no pasaba lo mismo.
 * Desplegar el 18 Pro Max mostraba UN renglón —el de 256 GB— no porque las
 * otras memorias estuvieran ocultas sino porque nunca se habían creado: el
 * desplegable listaba lo que había en la base y nada más.
 *
 * El resultado era que el hueco más caro del catálogo no se veía. Falta el
 * precio del 512 y del 1 TB del modelo que más se vende, y para descubrirlo
 * había que acordarse de que existen.
 *
 * Con esta tabla el desplegable se arma como la lista de arriba: el esqueleto
 * es lo que Apple hizo, y encima van las fichas que están cargadas.
 *
 * POR QUE NO ALCANZA CON UNA LISTA GENERICA
 * -----------------------------------------
 * `MEMORIAS` en `equipo.ts` es la lista de capacidades que PASAN POR EL LOCAL
 * —lo que el selector de la ficha ofrece— y eso es otra cosa. Usarla acá
 * pondría un renglón de 64 GB en el 18 Pro Max, que nunca existió, y dejaría
 * afuera los 2 TB, que sí. Un renglón de una memoria que Apple no fabricó es
 * peor que no tener el renglón: es una fila que nadie va a poder completar
 * nunca y que encima invita a cargar un precio inventado.
 *
 * LA MEMORIA ES LO QUE MUEVE EL PRECIO; EL COLOR NO
 * -------------------------------------------------
 * Por eso esta tabla arma renglones con costo y precio propios, y la de
 * colores sigue siendo un cuadradito. El 17 Pro Max negro y el naranja salen
 * lo mismo; el de 256 y el de 1 TB, no.
 *
 * COMO SE MANTIENE
 * ----------------
 * Igual que `COLORES_POR_MODELO`, que es su gemela: una vez por septiembre,
 * cuando Apple presenta. Las claves son las mismas de esa tabla —el modelo
 * como lo escribe `parsearIphone()`: "17 PRO MAX", "16E", "AIR"— y hay un test
 * que se cae si una tabla conoce un modelo que la otra no.
 */

/** 1 TB y 2 TB, en las unidades que guarda `memoria_gb`. */
const TB = 1024;

/**
 * Las capacidades de cada modelo, de menor a mayor, como las vendió Apple.
 *
 * Van en gigas porque así se guardan en `producto.memoria_gb`; quien las
 * muestra las escribe con `etiquetaCapacidad()`, que pasa sola a "1 TB".
 */
export const MEMORIAS_POR_MODELO: Record<string, ReadonlyArray<number>> = {
  "11": [64, 128, 256],
  "11 PRO": [64, 256, 512],
  "11 PRO MAX": [64, 256, 512],

  "12": [64, 128, 256],
  "12 MINI": [64, 128, 256],
  "12 PRO": [128, 256, 512],
  "12 PRO MAX": [128, 256, 512],

  "13": [128, 256, 512],
  "13 MINI": [128, 256, 512],
  "13 PRO": [128, 256, 512, TB],
  "13 PRO MAX": [128, 256, 512, TB],

  "14": [128, 256, 512],
  "14 PLUS": [128, 256, 512],
  "14 PRO": [128, 256, 512, TB],
  "14 PRO MAX": [128, 256, 512, TB],

  "15": [128, 256, 512],
  "15 PLUS": [128, 256, 512],
  "15 PRO": [128, 256, 512, TB],
  // El Pro Max arranca en 256: es el primero al que Apple le sacó el de 128.
  "15 PRO MAX": [256, 512, TB],

  "16": [128, 256, 512],
  "16 PLUS": [128, 256, 512],
  "16E": [128, 256, 512],
  "16 PRO": [128, 256, 512, TB],
  "16 PRO MAX": [256, 512, TB],

  // Del 17 para arriba la base es 256: no hay más modelos de 128. El 17e
  // tampoco, y ahí se nota: el 16e arrancaba en 128.
  "17": [256, 512],
  "17E": [256, 512],
  "17 PRO": [256, 512, TB],
  "17 PRO MAX": [256, 512, TB, 2 * TB],

  // Presentados el 09/09/2026. Los dos llevan las mismas cuatro: es la primera
  // vez que el Pro tambien llega a 2 TB (en el 17 el de 2 TB era solo el Max).
  "18 PRO": [256, 512, TB, 2 * TB],
  "18 PRO MAX": [256, 512, TB, 2 * TB],

  AIR: [256, 512, TB],
  // El plegable arranca en 256 y llega a 2 TB, como los Pro del mismo año.
  DUO: [256, 512, TB, 2 * TB],
  SE: [64, 128, 256],
};

/**
 * En qué memorias viene ese modelo.
 *
 * Un modelo que la tabla no conoce —un Samsung, un iPhone más viejo que el 11—
 * devuelve vacío, y el desplegable se queda con lo que esté cargado. Vacío es
 * la respuesta honesta: la alternativa sería ofrecer las capacidades de otro
 * teléfono.
 */
export function memoriasDelModelo(modelo: string): ReadonlyArray<number> {
  const clave = modelo.trim().toUpperCase().replace(/\s+/g, " ");
  return MEMORIAS_POR_MODELO[clave] ?? [];
}

/**
 * La memoria que queda al cambiar de modelo: la misma si el modelo nuevo vino
 * en esa capacidad, o ninguna. Pasar del 15 Pro Max (1 TB) al 15 base sin
 * borrarla dejaba cargado un teléfono que no existe, con el chip escondido.
 */
export function memoriaQueSigue(modelo: string, memoriaGb: number | null): number | null {
  if (memoriaGb === null) return null;
  const del = memoriasDelModelo(modelo);
  return del.length === 0 || del.includes(memoriaGb) ? memoriaGb : null;
}
